const translation = {
  title: 'Compute Clusters',
  create: {
    title: 'Create Compute Cluster',
  },
  detail: {
    title: 'Compute Cluster Details',
    tabs: {
      detail: 'Details',
      servers: 'Nodes',
      queues: 'Resource Queues',
      specs: 'Resource Specs',
      groups: 'Resource Groups',
      monitor: 'Monitor',
      server_specs: 'Node Specs',
      workloads: 'Workloads',
    },
  },
  form: {
    name: 'Cluster Name',
    nameTooltip: 'Unique name for the compute cluster, used to distinguish different clusters',
    description: 'Cluster Description',
    descriptionTooltip: 'Brief description of the cluster purpose and characteristics',
    type: 'Cluster Type',
    typeTooltip: 'Select the cluster type, which determines management method and feature capabilities',
    status: 'Cluster Status',
    statusTooltip: 'Current running status of the cluster, such as running, stopped, etc.',
    region: 'Region',
    regionTooltip: 'Geographic region of the cluster, affects network latency and data compliance',
    zone: 'Zone',
    zoneTooltip: 'Availability zone within the region, zones within the same region are interconnected',
    network: 'Network Configuration',
    networkTooltip: 'Network configuration including VPC, subnet settings',
    storage: 'Storage Configuration',
    storageTooltip: 'Storage configuration including storage type and mount methods',
    compute: 'Compute Configuration',
    computeTooltip: 'Compute resource configuration including CPU, GPU and memory',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    server_spec: 'Node Spec',

    // Form fields
    region_name: 'Region Name',
    region_name_tooltip: 'Identifier name of the cluster region, e.g. cn-east-1',
    region_alias: 'Region Alias',
    region_alias_tooltip: 'Human-readable alias for the region, e.g. "East China Zone 1"',
    server: 'Cluster Address',
    server_tooltip: 'Cluster address, e.g.: https://192.168.12.11:6443',
    runtime_sock_path: 'Sock',
    runtime_sock_path_tooltip:
      'Container runtime sock path, e.g.: /var/run/docker.sock',
    certificate: 'Service Certificate',
    certificate_tooltip: 'Kubernetes API Server CA certificate content for cluster identity verification',
    user_name: 'Username',
    user_name_tooltip: 'Username for authenticating with the Kubernetes cluster',
    user_key: 'User Key',
    user_key_tooltip: 'Secret key for authenticating with the Kubernetes cluster',
    user_certificate: 'User Certificate',
    user_certificate_tooltip: 'Client certificate content, used with the user key for authentication',
    user_token: 'User Token',
    user_token_tooltip: 'Kubernetes ServiceAccount Token for cluster API authentication',
    vip: 'VIP',
    vip_tooltip: 'Cluster traffic ingress IP or domain',
    harbor_registry: 'Harbor',
    harbor_registry_tooltip: 'Harbor address, e.g.: harbor.example.com',
    harbor_username: 'Harbor Username',
    harbor_username_tooltip: 'Username for logging into the Harbor image registry',
    harbor_password: 'Harbor Password',
    harbor_password_tooltip: 'Password for logging into the Harbor image registry',
    harbor_api_url: 'Harbor API URL',
    harbor_api_url_tooltip: 'Harbor API access URL, e.g. https://harbor.example.com/api/v2.0',
    ingress_host: 'Ingress Host',
    ingress_host_tooltip: 'Traffic ingress domain',
    internal_ingress_host: 'Internal Ingress Host',
    internal_ingress_host_tooltip: 'Internal service ingress domain name, used only in intranet environments',
    internal_ingress_port: 'Internal Ingress Port',
    internal_ingress_port_tooltip: 'Internal service ingress port number',
    use_tls: 'Use TLS',
    use_tls_tooltip: 'When enabled, cluster service ingress will use HTTPS encrypted communication',
    use_grafana: 'Use Grafana',
    use_grafana_tooltip: 'When enabled, you can view cluster monitoring dashboards and visual reports via Grafana',
    prometheus_url: 'Prometheus',
    prometheus_url_tooltip: 'Prometheus monitoring service URL for collecting and querying cluster metrics',
    docker_image: 'Docker Image',
    docker_image_tooltip: 'Docker CLI tool image used in the cluster for building and managing container images',
    docker_image_placeholder: 'dodocker:23.0.4',
    nerdctl_image: 'Nerdctl Image',
    nerdctl_image_tooltip: 'Nerdctl tool image used in the cluster for containerd runtime image management',
    nerdctl_image_placeholder:
      'ccr.ccs.tencentyun.com/cube-studio/nerdctl:1.7.2',
    cloudshell_image: 'Cloudtty Image',
    cloudshell_image_tooltip: 'Cloudtty image for Web Terminal functionality, allowing users to log into containers via browser',
    cloudshell_image_placeholder: '',
    kaniko_image: 'KANIKO Image',
    kaniko_image_tooltip: 'Kaniko tool image for unprivileged container image building in Kubernetes',
    kaniko_image_placeholder: 'gcr.io/kaniko-project/executor:v1.6.0',
    data_importer_image: 'Data Importer Image',
    data_importer_image_tooltip: 'Tool image for importing external data (e.g. Git repos) into platform storage',
    data_importer_image_placeholder:
      'registry.cn-hangzhou.aliyuncs.com/trainkr-public/dataset-importer',

    // Action messages
    create_success: 'Create compute cluster successfully!',
    create_failed: 'Create compute cluster failed!',
    update_success: 'Update compute cluster successfully!',
    update_failed: 'Update compute cluster failed!',
  },
  list: {
    title: 'Compute Cluster List',
    create: 'Create Cluster',
    search: 'Search Clusters',
    filter: 'Filter',
    // Table column headers
    name: 'Name',
    status: 'Status',
    region: 'Region',
    server: 'Server',
    username: 'Username',
    default_cluster: 'Default Cluster',
    sock: 'Sock',
    created_at: 'Created At',
    updated_at: 'Updated At',
    operation: 'Operation',

    // Buttons and actions
    create_cluster: 'Create Compute Cluster',
    view_servers: 'View Servers',
    edit: 'Edit',
    delete: 'Delete',
    search_placeholder: 'Search',

    // Status values
    yes: 'Yes',
    no: 'No',

    // Action confirmations
    delete_confirm: 'Confirm Delete',
    delete_confirm_content: 'Are you sure to delete this cluster?',
    delete_success: 'Delete succeeded!',
    delete_failed: 'Delete failed!',
    confirm: 'Confirm',
    cancel: 'Cancel',

    status_values: {
      active: 'Running',
      inactive: 'Stopped',
      error: 'Error',
      pending: 'Creating',
      deleting: 'Deleting',
    },
    actions: {
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      start: 'Start',
      stop: 'Stop',
      restart: 'Restart',
    },
  },
  servers: {
    title: 'Node Management',
    add: 'Add Node',
    remove: 'Remove Node',
    // Server status
    status_values: {
      running: 'Running',
      stopped: 'Stopped',
      error: 'Error',
      pending: 'Creating',
    },
    // Server list table column headers
    id: 'ID',
    status: 'Status',
    resource_group: 'Resource Group',
    gpu_name: 'GPU Model',
    gpu_num: 'GPU Count',
    gpu_mem: 'GPU Memory',
    total_resources: 'Total Resources',
    available_resources: 'Available Resources',
    ip: 'IP',
    created_at: 'Created At',
    updated_at: 'Updated At',
    operation: 'Operation',
    edit: 'Edit',
    // Search related
    search_placeholder: 'Search servers',

    // Server form fields
    form: {
      name: 'Name',
      alias: 'Alias',
      status: 'Status',
      resource_group: 'Resource Group',
      description: 'Description',
      gpu_vendor: 'GPU Vendor',
      gpu_name: 'GPU Name',
      gpu_num: 'GPU Count',
      gpu_mem: 'GPU Memory',
      gpu_spp: 'GPU SPP',
      gpu_dpp: 'GPU DPP',
      max_cuda: 'Max CUDA Version',
      pay_price_hourly: 'Hourly Price',
      cpu_num: 'CPU Count',
      cpu_name: 'CPU Name',
      mem_size: 'Memory Size',
      disk_size: 'Disk Size',
      disk_type: 'Disk Type',
      ip: 'IP Address',
      labels: 'Labels',
    },

    // Buttons and actions
    confirm: 'Confirm',
    cancel: 'Cancel',

    // Action messages
    update_server: 'Update Compute Node',
    create_server: 'Create Compute Node',
  },
  queues: {
    title: 'Resource Queues',
    add: 'Add Queue',
    remove: 'Remove Queue',
    name: 'Queue Name',
    priority: 'Priority',
    status: {
      active: 'Active',
      inactive: 'Inactive',
      error: 'Error',
    },
    // Queue list table column headers
    id: 'ID',
    permission: 'Permission',
    status_label: 'Status',
    state: 'Running Status',
    weight: 'Weight',
    priority_label: 'Priority',
    job_status: 'Job Status',
    capability: 'Resource Capacity',
    allocated: 'Allocated',
    reservation: 'Reservation',
    reclaimable: 'Resource Reclaim',
    created_at: 'Created At',
    updated_at: 'Updated At',
    operation: 'Operation',

    // Job status
    inqueue: 'In Queue',
    running: 'Running',
    pending: 'Pending',
    completed: 'Completed',

    // Buttons and actions
    create_queue: 'Create Queue',
    edit: 'Edit',
    delete: 'Delete',

    // Action confirmations
    delete_confirm: 'Confirm Delete',
    delete_confirm_content: 'Are you sure to delete this queue?',
    delete_success: 'Delete succeeded!',
    delete_failed: 'Delete failed!',

    // Resource reclaim
    yes: 'Yes',
    no: 'No',
  },
  specs: {
    title: 'Resource Specs',
    add: 'Add Spec',
    remove: 'Remove Spec',
    name: 'Spec Name',
    cpu: 'CPU',
    memory: 'Memory',
    gpu: 'GPU',
    storage: 'Storage',
    // Resource spec list
    create_resource_spec: 'Create Resource Spec',
    search_placeholder: 'Search',
    // Table column headers
    id: 'ID',
    gpu_name: 'GPU Model',
    gpu_num: 'GPU Count',
    gpu_mem: 'GPU Memory(GB)',
    cpu_num: 'CPU Cores',
    mem_size: 'Memory Size(GB)',
    created_at: 'Created At',
    updated_at: 'Updated At',
    operation: 'Operation',
    // Action buttons
    edit: 'Edit',
    delete: 'Delete',
  },
  groups: {
    title: 'Resource Groups',
    add: 'Add Resource Group',
    remove: 'Remove Resource Group',
    name: 'Group Name',
    description: 'Group Description',
    members: 'Members',
    // Resource group form
    form: {
      name: 'Name',
      nameTooltip: 'Unique name for the resource group, used for grouping cluster nodes',
      description: 'Description',
      descriptionTooltip: 'Brief description of the resource group purpose and contained resources',
      cluster_id: 'Cluster ID',
      confirm: 'Confirm',
      cancel: 'Cancel',
      create_success: 'Create resource group successfully!',
      create_failed: 'Create resource group failed!',
      update_success: 'Update resource group successfully!',
      update_failed: 'Update resource group failed!',
      create_action: 'Create Resource Group',
      update_action: 'Update Resource Group',
    },
    // Resource group list
    list: {
      title: 'Resource Group List',
      create: 'Create Resource Group',
      search: 'Search',
      search_placeholder: 'Search',
      // Table column headers
      id: 'ID',
      name: 'Name',
      description: 'Description',
      created_at: 'Created At',
      updated_at: 'Updated At',
      operation: 'Operation',
      // Action buttons
      edit: 'Edit',
      delete: 'Delete',
      // Action confirmations
      delete_confirm: 'Confirm Delete',
      delete_confirm_content: 'Are you sure to delete this resource group?',
      delete_success: 'Delete succeeded!',
      delete_failed: 'Delete failed!',
    },
  },
  monitor: {
    title: 'Cluster Monitor',
    overview: 'Overview',
    performance: 'Performance',
    resources: 'Resource Usage',
    alerts: 'Alerts',
    logs: 'Logs',
  },
  overview: {
    home: 'Home',
    workload: 'Workload',
    storage: 'Storage',
    other: 'Other',
    containerInstance: 'Container Instance',
    trainTask: 'Training Task',
    inferenceService: 'Inference Service',
    model: 'Model',
    dataset: 'Dataset',
    image: 'Image',
    disk: 'Disk',
    repository: 'Repository',
    userCenter: 'User Center',
    welcome: 'Welcome to {{name}}',
  },
  resource_spec_form: {
    cluster_id: 'Cluster ID',
    name: 'Name',
    name_tooltip: 'Unique name for the resource specification, e.g. gpu-a100-80g-8',
    gpu_name: 'GPU Model',
    gpu_name_tooltip: 'GPU model, e.g. RTX-3090, A100',
    gpu_name_placeholder: 'RTX-3090',
    gpu_key: 'GPU Key',
    gpu_key_tooltip: 'Key for identifying GPU resource in Kubernetes, usually nvidia.com/<model>',
    gpu_key_placeholder: 'nvidia.com/rtx-3090',
    gpu_num: 'GPU Count',
    gpu_num_tooltip: 'Number of GPUs in this spec',
    gpu_mem: 'GPU Memory',
    gpu_mem_tooltip: 'GPU memory size, e.g. 24Gi, 80Gi',
    cpu_num: 'CPU Cores',
    cpu_num_tooltip: 'Number of CPU cores in this spec',
    cpu_name: 'CPU Model',
    cpu_name_tooltip: 'CPU model, e.g. Intel Xeon Gold 6258R',
    cpu_arch: 'CPU Architecture',
    cpu_arch_tooltip: 'CPU architecture, e.g. amd64, arm64',
    mem_size: 'Memory Size',
    mem_size_tooltip: 'Memory size, e.g. 64Gi, 128Gi',
    system_disk: 'System Disk',
    train_template_id: 'Training Task Template ID',
    instance_template_id: 'Container Instance Template ID',
    deployment_template_id: 'Inference Service Template ID',
    extras: 'Other Resources',
    extras_tooltip: 'Additional resources, e.g. TPU, FPGA',
    confirm: 'Confirm',
    cancel: 'Cancel',
    create: 'Create Resource Spec',
    update: 'Update Resource Spec',
  },
  server_spec: {
    detail_title: 'Node Spec Detail',
    create_title: 'Create Node Spec',
    edit_title: 'Edit Node Spec',
  },
  // ClusterList.tsx
  ClusterList: {
    // Table column headers
    name: 'Name',
    status: 'Status',
    region: 'Region',
    server: 'Server',
    username: 'Username',
    default_cluster: 'Default Cluster',
    sock: 'Sock',
    created_at: 'Created At',
    updated_at: 'Updated At',
    operation: 'Operation',

    // Buttons and actions
    create_cluster: 'Create Compute Cluster',
    view_servers: 'View Servers',
    edit: 'Edit',
    delete: 'Delete',
    search_placeholder: 'Search',

    // Status values
    yes: 'Yes',
    no: 'No',

    // Action confirmations
    delete_confirm: 'Confirm Delete',
    delete_confirm_content: 'Are you sure to delete this cluster?',
    delete_success: 'Delete succeeded!',
    delete_failed: 'Delete failed!',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },

  // ResourceMonitor.tsx
  ResourceMonitor: {
    title: 'Resource Monitor',
    compute: 'Compute',
    storage: 'Disk',
    harbor: 'Harbor',
  },
}

export default translation
